import { useAuth } from '@/shared/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from './use-toast';
import { useEffect } from 'react';

export type UserRole = 'admin' | 'user' | 'super_admin';

export const useRoleGuard = () => {
  const { userRole, isLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const currentRole = (userRole?.role || 'user') as UserRole;

  const isAdmin = currentRole === 'admin' || currentRole === 'super_admin';
  const isSuperAdmin = currentRole === 'super_admin';

  const requireRole = (allowedRoles: UserRole[], redirectTo: string = '/dashboard') => {
    // Don't check permissions while still loading
    if (isLoading) return false;

    // CRITICAL FIX: If userRole is null (not loaded yet), don't redirect
    // This prevents premature redirects when the component mounts before auth data loads
    if (userRole === null || userRole === undefined) {
      console.warn('[useRoleGuard] userRole is null - waiting for auth data to load');
      return false;
    }

    if (!allowedRoles.includes(currentRole)) {
      console.error('[useRoleGuard] Access denied:', {
        currentRole,
        allowedRoles,
        userRole,
      });
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to access this feature. Only admins can manage billing and team settings.',
        variant: 'destructive',
      });
      navigate(redirectTo);
      return false;
    }

    return true;
  };

  const requireAdmin = (redirectTo: string = '/dashboard') => {
    return requireRole(['admin', 'super_admin'], redirectTo);
  };

  const requireSuperAdmin = (redirectTo: string = '/dashboard') => {
    return requireRole(['super_admin'], redirectTo);
  };

  return {
    currentRole,
    isAdmin,
    isSuperAdmin,
    requireRole,
    requireAdmin,
    requireSuperAdmin,
  };
};

/**
 * Hook to protect routes that require specific roles
 * Usage: useRequireRole(['admin', 'super_admin'])
 */
export const useRequireRole = (allowedRoles: UserRole[], redirectTo: string = '/dashboard') => {
  const { requireRole, isLoading } = useRoleGuard();

  useEffect(() => {
    if (!isLoading) {
      requireRole(allowedRoles, redirectTo);
    }
  }, [isLoading, allowedRoles, redirectTo]);
};

/**
 * Hook to protect admin-only routes
 * Usage: useRequireAdmin()
 */
export const useRequireAdmin = (redirectTo: string = '/dashboard') => {
  useRequireRole(['admin', 'super_admin'], redirectTo);
};
